import { format } from 'date-fns';
import { enGB, fr } from 'date-fns/locale';
import {
  ComponentProps,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
  WheelEvent,
} from 'react';
import { DayPicker, useDayPicker, useNavigation } from 'react-day-picker';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../assets/icons/index';
import { useLocale } from '../../hooks/index';
import { Locale } from '../../lang/index';
import { toUpperCaseWords } from '../../utils/to-uppercase-util.util';
import Button from '../button/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../select/index';

export type CalendarProps = {
  defaultMonth?: Date;
} & ComponentProps<typeof DayPicker>;

const CAPTION_DROPDOWN_OPTIONS_CLASS =
  'z-[100] origin-top gap-0 overflow-hidden rounded-md border border-gray-200 bg-white p-0 shadow-lg animate-in fade-in-0 zoom-in-95 duration-150';
const CAPTION_DROPDOWN_SCROLL_CLASS =
  'scrollbar-always-visible max-h-60 min-h-0 overflow-y-auto overscroll-contain py-1';

const stopNestedScrollPropagation = (event: WheelEvent<HTMLDivElement>) => {
  event.stopPropagation();
};

// Callers pass API values typed as `Date` that are JSON strings at runtime, and date-fns turns
// anything but a Date or a timestamp into an Invalid Date, so normalize before DayPicker reads it.
const toCalendarDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value as number | string);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

// Forms seed a date field nobody has picked yet with `new Date(+0)` and treat that epoch as empty
// everywhere else — validation rejects it and inputs render it blank — so it is not a day to
// select or to open on.
const isEmptyDate = (date: Date): boolean => date.getTime() === new Date(+0).getTime();

const toChosenDate = (value: unknown): Date | undefined => {
  const date = toCalendarDate(value);
  return date && !isEmptyDate(date) ? date : undefined;
};

const isDateRange = (value: unknown): value is { from?: unknown; to?: unknown } =>
  !!value &&
  typeof value === 'object' &&
  !(value instanceof Date) &&
  ('from' in value || 'to' in value);

/** A DayPicker matcher holds a day, a list of days or a range — pull the plain dates out of it. */
const collectDates = (matcher: unknown): Date[] => {
  if (Array.isArray(matcher)) return matcher.flatMap(collectDates);
  if (isDateRange(matcher)) return [matcher.from, matcher.to].flatMap(collectDates);
  const date = toChosenDate(matcher);
  return date ? [date] : [];
};

/** Same shape as the given matcher, with the days the user actually picked usable by date-fns. */
const normalizeSelected = <T,>(selected: T): T => {
  if (Array.isArray(selected)) return selected.map(normalizeSelected) as T;
  if (isDateRange(selected)) {
    return {
      ...selected,
      from: toChosenDate(selected.from),
      to: toChosenDate(selected.to),
    } as T;
  }
  const date = toCalendarDate(selected);
  // Anything that is not a date — a predicate or a `{ before }` matcher — passes through as is.
  if (!date) return selected;
  return (isEmptyDate(date) ? undefined : date) as T;
};

const DEFAULT_CAPTION_YEAR_LOOKBACK = 30;
const DEFAULT_CAPTION_YEAR_LOOKAHEAD = 10;

type CaptionYearRangeInput = {
  /** Years the open month / current value sit in — expand the default window to keep them listed. */
  anchorYears?: number[];
  fromDate?: Date;
  toDate?: Date;
};

// The dropdown is just a jump list. Selectable days still come from DayPicker `fromDate` /
// `toDate` / `disabled` — shrinking the list must not widen those constraints.
export const resolveCaptionYearRange = ({
  fromDate,
  toDate,
  anchorYears = [],
}: CaptionYearRangeInput): { maxYear: number; minYear: number } => {
  const currentYear = new Date().getFullYear();

  let minYear = fromDate?.getFullYear() ?? currentYear - DEFAULT_CAPTION_YEAR_LOOKBACK;
  let maxYear = toDate?.getFullYear() ?? currentYear + DEFAULT_CAPTION_YEAR_LOOKAHEAD;

  if (!fromDate) {
    for (const year of anchorYears) {
      minYear = Math.min(minYear, year);
    }
  }
  if (!toDate) {
    for (const year of anchorYears) {
      maxYear = Math.max(maxYear, year);
    }
  }

  if (minYear > maxYear) {
    minYear = maxYear;
  }

  return { minYear, maxYear };
};

type CaptionDropdownOptionsProps = {
  children: ReactNode;
  className?: string;
  open: boolean;
};

const CaptionDropdownOptions: FC<CaptionDropdownOptionsProps> = ({ children, className, open }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      scrollRef.current
        ?.querySelector('[aria-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
    });

    return () => cancelAnimationFrame(frame);
  }, [open]);

  return (
    <SelectOptions
      className={twMerge(CAPTION_DROPDOWN_OPTIONS_CLASS, className)}
      // Headless UI makes the page inert while an open listbox is modal, which would freeze the
      // rest of the caption and the day grid behind it.
      modal={false}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div
        className={CAPTION_DROPDOWN_SCROLL_CLASS}
        onWheel={stopNestedScrollPropagation}
        ref={scrollRef}
      >
        {children}
      </div>
    </SelectOptions>
  );
};

type CaptionSelectOption = {
  label: string;
  value: string;
};

type CaptionSelectProps = {
  onChange: (value: string) => void;
  options: CaptionSelectOption[];
  optionsClassName?: string;
  value: string;
};

const CaptionSelect: FC<CaptionSelectProps> = ({ onChange, options, optionsClassName, value }) => {
  const selectedLabel = options.find((option) => option.value === value)?.label ?? value;
  const longestLabel = options.reduce(
    (longest, option) => (option.label.length > longest.length ? option.label : longest),
    selectedLabel,
  );

  return (
    <Select onChange={onChange} value={value}>
      {({ open, value: selectedValue }) => (
        <div className="relative">
          <SelectButton
            className={twMerge(
              'flex items-center gap-1 rounded border-none bg-gray-50 py-1 pl-2 pr-1 shadow-none transition-colors duration-150',
              open && 'bg-gray-100',
            )}
          >
            {/* Size to the longest option so May → September does not shift the arrows. */}
            <span className="grid text-sm font-[550] text-gray-700">
              <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-nowrap">
                {longestLabel}
              </span>
              <span className="col-start-1 row-start-1 whitespace-nowrap">
                {options.find((option) => option.value === selectedValue)?.label ?? selectedValue}
              </span>
            </span>
            <ChevronDownIcon
              className={twMerge(
                'size-3.5 shrink-0 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </SelectButton>

          {open && (
            <CaptionDropdownOptions
              className={twMerge('left-0 w-full min-w-0', optionsClassName)}
              open={open}
            >
              {options.map((option) => (
                <SelectOption
                  className={twMerge(
                    'h-8 truncate rounded px-1.5 py-1 text-sm text-gray-700 transition-colors duration-100',
                    selectedValue === option.value
                      ? '!bg-brand-700 text-white hover:!bg-brand-700'
                      : 'hover:bg-gray-100',
                  )}
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </SelectOption>
              ))}
            </CaptionDropdownOptions>
          )}
        </div>
      )}
    </Select>
  );
};

const DropdownsCaption = (): ReactElement => {
  const { currentMonth, goToMonth } = useNavigation();
  const { fromDate, toDate, selected } = useDayPicker();
  const { locale } = useLocale();

  const { minYear, maxYear } = resolveCaptionYearRange({
    // Anchor on the open month too, so navigating past the default window keeps that year listed.
    anchorYears: [currentMonth, ...collectDates(selected)].map((date) => date.getFullYear()),
    fromDate,
    toDate,
  });

  const lang = locale === Locale.FR ? fr : enGB;
  const months = Array.from({ length: 12 }, (_, i) =>
    format(new Date(0, i), 'LLLL', { locale: lang }),
  );
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, index) => maxYear - index);

  const handlePreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() - 1);
    goToMonth(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() + 1);
    goToMonth(newDate);
  };

  const handleYearChange = (value: string) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(parseInt(value, 10));
    goToMonth(newDate);
  };

  const handleMonthChange = (value: string) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(parseInt(value, 10));
    goToMonth(newDate);
  };

  const monthOptions = months.map((monthName, index) => ({
    label: toUpperCaseWords(monthName) ?? monthName,
    value: String(index),
  }));
  const yearOptions = years.map((year) => ({
    label: String(year),
    value: String(year),
  }));

  return (
    <div className="flex h-8 w-full shrink-0 items-center justify-between gap-1">
      <Button
        aria-label="Previous month"
        className="flex h-full w-fit shrink-0 items-center justify-center p-1"
        onClick={handlePreviousMonth}
        styleType="NONE"
      >
        <ChevronDownIcon className="size-5 rotate-90" />
      </Button>

      <div className="flex items-center gap-1.5">
        <CaptionSelect
          onChange={handleMonthChange}
          options={monthOptions}
          optionsClassName="min-w-[100px]"
          value={String(currentMonth.getMonth())}
        />

        <CaptionSelect
          onChange={handleYearChange}
          options={yearOptions}
          value={String(currentMonth.getFullYear())}
        />
      </div>

      <Button
        aria-label="Next month"
        className="flex h-full w-fit shrink-0 items-center justify-center p-1"
        onClick={handleNextMonth}
        styleType="NONE"
      >
        <ChevronDownIcon className="size-5 -rotate-90" />
      </Button>
    </div>
  );
};

// A new component identity here would remount the caption on every render and drop the open
// dropdown, so the caption reads what it needs from the DayPicker context instead of props.
const CALENDAR_COMPONENTS = { Caption: DropdownsCaption };

export const Calendar: FC<CalendarProps> = ({
  className,
  classNames,
  showOutsideDays = true,
  defaultMonth,
  fromDate,
  toDate,
  formatters,
  initialFocus,
  ...props
}) => {
  const { locale } = useLocale();
  const lang = locale === Locale.FR ? fr : enGB;

  // DayPicker focuses its day from a child effect, which runs before the enclosing Popover's
  // FocusScope pauses a Dialog / Sheet focus trap behind it. The trap then pulls focus back to its
  // last focused element — in Safari, where a clicked button never takes focus, that is the dialog
  // itself, so the popover sees focus outside and closes as it opens. One frame later the trap is
  // paused and the day can take focus.
  const [isInitialFocusReady, setInitialFocusReady] = useState(false);

  useEffect(() => {
    if (!initialFocus) return;

    const frame = requestAnimationFrame(() => setInitialFocusReady(true));
    return () => cancelAnimationFrame(frame);
  }, [initialFocus]);

  // The cast keeps DayPicker's `mode`-discriminated props intact: normalizing never changes the
  // shape of `selected`, but TS cannot tell which branch of the union it belongs to here.
  const dayPickerProps = {
    ...props,
    selected: normalizeSelected(props.selected),
  } as typeof props;

  // Reopening a picker has to land on the date already chosen, not on today.
  const openMonth = toChosenDate(defaultMonth) ?? collectDates(dayPickerProps.selected)[0];

  return (
    <DayPicker
      captionLayout="dropdown"
      className={twMerge('p-3', className)}
      classNames={{
        months: 'flex w-full flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'w-full space-y-3',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-gray-700 font-[550] text-sm',
        nav: 'space-x-1 flex items-center',
        nav_button: twMerge(
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 disabled:hidden',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse',
        head_row: 'flex w-full justify-between',
        head_cell: 'w-8 font-normal text-[0.8rem] text-gray-500',
        row: 'mt-2 flex w-full justify-between',
        cell: twMerge(
          'relative h-8 w-8 rounded-full p-0 text-center text-sm',
          '[&:has([aria-selected].day-range-end)]:rounded-r-md',
          '[&:has([aria-selected].day-outside)]:bg-accent/50',
          'first:[&:has([aria-selected])]:rounded-l-md',
          'last:[&:has([aria-selected])]:rounded-r-md',
          'focus-within:relative focus-within:z-20',
          'hover:bg-gray-200',
        ),
        day: 'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
        day_range_end: 'day-range-end rounded-l-md',
        day_range_start: 'day-range-start rounded-r-md',
        day_selected:
          'rounded-full bg-brand-700 text-white hover:bg-brand-700 hover:text-white focus:bg-brand-700 focus:text-white',
        day_today: twMerge(
          '[&:not(.day-selected):not(.day-range-middle):not(.day-range-start):not(.day-range-end)]:border-[1px] border-gray-700 rounded-full',
        ),
        day_outside: 'day-outside !text-gray-400 hover:!text-gray-400 aria-selected:!text-white',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'day-range-middle aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none !bg-brand-25 !text-gray-700',
        day_hidden: 'invisible',
        ...classNames,
      }}
      defaultMonth={openMonth}
      formatters={{
        formatWeekdayName: (date) => format(date, 'EEEEE', { locale: lang }),
        ...formatters,
      }}
      fromDate={toCalendarDate(fromDate)}
      locale={lang}
      toDate={toCalendarDate(toDate)}
      {...dayPickerProps}
      components={CALENDAR_COMPONENTS}
      fixedWeeks
      initialFocus={initialFocus && isInitialFocusReady}
      showOutsideDays={showOutsideDays}
    />
  );
};
